import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { getDocument } from '@/lib/utils';

const isPointInside = (el: HTMLElement | null, x: number, y: number): boolean => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

export function useStickyHover(
  hideDelayMs = 150,
  trackedRefs?: ReadonlyArray<RefObject<HTMLElement | null>>,
) {
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsHovered(true);
  }, []);

  const scheduleHide = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsHovered(false);
    }, hideDelayMs);
  }, [hideDelayMs]);

  useEffect(() => {
    const clearPendingHide = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const doc = getDocument();
    if (!isHovered || !trackedRefs?.length || !doc) return clearPendingHide;
    const handler = (e: PointerEvent) => {
      const inside = trackedRefs.some((r) => isPointInside(r.current, e.clientX, e.clientY));
      if (inside) cancelHide();
      else scheduleHide();
    };
    doc.addEventListener('pointermove', handler);
    return () => {
      doc.removeEventListener('pointermove', handler);
      clearPendingHide();
    };
  }, [isHovered, trackedRefs, cancelHide, scheduleHide]);

  return { isHovered, cancelHide, scheduleHide };
}
